import { cva } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Icon } from './icon.tsx'
import { InputText } from './input-text.tsx'
import { type IconName } from '@/icon-name'

// Tipos para las opciones del Select
export interface SelectOption {
	value: string
	label: string
	icon?: IconName
	disabled?: boolean
}

// Props del componente Select
export interface SelectProps {
	/**
	 * Lista de opciones
	 */
	options: SelectOption[]
	/**
	 * Valor seleccionado
	 */
	value?: string
	/**
	 * Valor por defecto
	 */
	defaultValue?: string
	/**
	 * Callback cuando cambia la selección
	 */
	onValueChange?: (value: string) => void
	/**
	 * Texto placeholder
	 */
	placeholder?: string
	/**
	 * Si permite búsqueda/filtrado
	 */
	searchable?: boolean
	/**
	 * Si está deshabilitado
	 */
	disabled?: boolean
	/**
	 * Clase CSS adicional
	 */
	className?: string

	// Props del InputText trigger
	/**
	 * Label del campo
	 */
	label?: string
	/**
	 * Posición del label
	 */
	labelPosition?: 'up' | 'internal'
	/**
	 * Tamaño del select
	 */
	size?: 's' | 'm' | 'l'
	/**
	 * Texto de soporte
	 */
	supportingText?: string
	/**
	 * Texto de error
	 */
	errorText?: string
	/**
	 * Texto del tooltip
	 */
	tooltipText?: string
	/**
	 * Si es requerido
	 */
	required?: boolean
}

// Variantes para los items del select
const selectItemVariants = cva(
	'relative flex cursor-pointer items-center transition-colors select-none focus:outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
	{
		variants: {
			size: {
				s: 'px-3 py-2 text-sm',
				m: 'px-4 py-3 text-sm',
				l: 'px-4 py-3 text-base',
			},
		},
		defaultVariants: {
			size: 'm',
		},
	},
)

const SelectComponent = React.forwardRef<HTMLDivElement, SelectProps>(
	(
		{
			options,
			value,
			defaultValue,
			onValueChange,
			placeholder = 'Seleccionar opción',
			searchable = false,
			disabled = false,
			className,

			// InputText props
			label,
			labelPosition = 'up',
			size = 'm',
			supportingText,
			errorText,
			tooltipText,
			required = false,

			...props
		},
		ref,
	) => {
		const [open, setOpen] = React.useState(false)
		const [searchValue, setSearchValue] = React.useState('')
		const [internalValue, setInternalValue] = React.useState(defaultValue || '')
		const inputTextRef = React.useRef<HTMLDivElement>(null)

		// Valor controlado vs no controlado
		const currentValue = value !== undefined ? value : internalValue

		// Filtrar opciones si es searchable
		const filteredOptions = React.useMemo(() => {
			if (!searchable || !searchValue) return options
			return options.filter((option) =>
				option.label.toLowerCase().includes(searchValue.toLowerCase()),
			)
		}, [options, searchable, searchValue])

		// Encontrar la opción seleccionada para mostrar en el trigger
		const selectedOption = React.useMemo(() => {
			return options.find((option) => option.value === currentValue)
		}, [options, currentValue])

		// Manejar selección de opción
		const handleSelect = (optionValue: string) => {
			if (value === undefined) {
				setInternalValue(optionValue)
			}
			onValueChange?.(optionValue)
			setOpen(false)
			setSearchValue('')
		}

		// Cerrar cuando se hace click afuera
		React.useEffect(() => {
			const handleClickOutside = (event: MouseEvent) => {
				if (
					ref &&
					'current' in ref &&
					ref.current &&
					!ref.current.contains(event.target as Node)
				) {
					setOpen(false)
				}
			}

			if (open) {
				document.addEventListener('mousedown', handleClickOutside)
				return () =>
					document.removeEventListener('mousedown', handleClickOutside)
			}
		}, [open, ref])

		return (
			<div ref={ref} className={cn('relative', className)} {...props}>
				<InputText
					ref={inputTextRef}
					label={label}
					labelPosition={labelPosition}
					size={size}
					supportingText={supportingText}
					errorText={errorText}
					tooltipText={tooltipText}
					required={required}
					showIconL={searchable}
					iconL={searchable ? 'magnifying-glass' : undefined}
					showDropdown={true}
					value={selectedOption ? selectedOption.label : ''}
					placeholder={placeholder}
					readOnly
					disabled={disabled}
					className="w-full cursor-pointer"
					data-state={open ? 'open' : 'closed'}
					aria-expanded={open}
					tabIndex={disabled ? -1 : 0}
					onClick={() => !disabled && setOpen(!open)}
				/>

				{open && !disabled && (
					<>
						{/* Overlay para cerrar al hacer click afuera */}
						<div
							className="fixed inset-0 z-40"
							onClick={() => setOpen(false)}
						/>

						{/* Dropdown content */}
						<div
							className={cn(
								'absolute z-50 mt-1 w-full',
								// Estilos exactos de Figma
								'flex flex-col items-start justify-start gap-1 rounded-lg bg-[var(--neutral-50)] p-2 shadow-[0px_5px_10px_0px_rgba(217,217,217,1.00)]',
								'border border-[var(--neutral-300)]',
							)}
						>
							{searchable && (
								<div className="mb-1 w-full">
									<input
										type="text"
										placeholder="Buscar..."
										value={searchValue}
										onChange={(e) => setSearchValue(e.target.value)}
										className="w-full rounded-lg border border-[var(--neutral-500)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--blue-500)] focus:shadow-[0_0_0_2px_var(--blue-100)]"
										autoFocus
										onClick={(e) => e.stopPropagation()}
									/>
								</div>
							)}

							<div className="max-h-48 w-full overflow-y-auto">
								{filteredOptions.length === 0 ? (
									<div className="px-4 py-3 text-sm text-[var(--neutral-600)]">
										No se encontraron opciones
									</div>
								) : (
									filteredOptions.map((option) => (
										<div
											key={option.value}
											onClick={(e) => {
												e.stopPropagation()
												if (!option.disabled) {
													handleSelect(option.value)
												}
											}}
											className={cn(
												selectItemVariants({ size }),
												// Estilos exactos de Figma
												'inline-flex w-full items-center justify-start gap-3 rounded-lg',
												'text-[var(--blue-900)] hover:bg-[var(--blue-100)] focus:bg-[var(--blue-100)]',
												'cursor-pointer select-none',
												currentValue === option.value && 'bg-[var(--blue-100)]',
												option.disabled && 'cursor-not-allowed opacity-50',
											)}
										>
											{/* Icono izquierdo */}
											{option.icon && (
												<div className="flex h-4 w-4 shrink-0 items-center justify-center">
													<Icon
														name={option.icon}
														size="sm"
														className="text-[var(--blue-800)]"
													/>
												</div>
											)}

											{/* Texto de la opción */}
											<div className="flex-1 text-left">{option.label}</div>

											{/* Indicador de selección */}
											{currentValue === option.value && (
												<div className="flex h-4 w-4 items-center justify-center">
													<Icon
														name="check"
														size="sm"
														className="text-[var(--blue-800)]"
													/>
												</div>
											)}
										</div>
									))
								)}
							</div>
						</div>
					</>
				)}
			</div>
		)
	},
)

SelectComponent.displayName = 'Select'

export { SelectComponent as Select }
